import { logger } from './logger';
import { blurInPlace, coverWithBitmap, DEFAULT_BLUR_PX, } from './overlay';
import { showRevealTransition } from './reveal-blur';
import { coverWithScaledClone, isSnapshotMaskNode, prepareScaledClone, } from './scale-overlay';
import { nearDims, sameDims, SnapshotStore, withRenderTimeout, } from './snapshot-store';
const MAX_SETTLE_RELAYOUTS = 3;
const DEFAULT_SWAP_TOLERANCE_PX = 24;
const PREPARED_CLONE_TOLERANCE_PX = 10;
const BLUR_DEADBAND_RATIO = 0.01;
const BLUR_FULL_AT_RATIO = 0.08;
const FREEZE_SEED_BLUR_PX = 2;
const stretchBlurPx = (captured, displayed, maxPx) => {
    const deviation = Math.max(Math.abs(displayed.width / captured.width - 1), Math.abs(displayed.height / captured.height - 1));
    if (deviation < BLUR_DEADBAND_RATIO)
        return 0;
    const ramp = Math.min(1, (deviation - BLUR_DEADBAND_RATIO) /
        (BLUR_FULL_AT_RATIO - BLUR_DEADBAND_RATIO));
    return Math.round(ramp * maxPx * 10) / 10;
};
export const DEFAULT_REVEAL_BLUR_MS = 1000;
export const DEFAULT_REVEAL_EASING = 'cubic-bezier(0.65, 0, 0.35, 1)';
const DEFAULT_REVEAL_CROSSFADE_MS = 400;
const DEFAULT_REVEAL_CROSSFADE_EASING = 'ease-out';
const CACHE_MAX = 2;
const CACHE_EVICT_POLL_MS = 5000;
const CACHE_FLUSH_MS = 20000;
const SCALE_UPDATE_COALESCE_MS = 50;
const sizeKey = (dims) => `${Math.round(dims.width)}x${Math.round(dims.height)}`;
export class SnapshotManagerV2 {
    adapter;
    enabled;
    freezeWithoutFinalSize;
    swapTolerancePx;
    blurTransition;
    blurOnly;
    blurPx;
    scaleResize;
    bitmapCapable;
    scaledBurst = false;
    preparedScaleClone = null;
    scaleUpdateTimer = null;
    lastScaleUpdateAt = 0;
    revealBlur;
    revealCrossfade;
    store;
    overlay = null;
    revealFade = null;
    frozenDims = null;
    lastBlurPx = 0;
    seedBlurPx = 0;
    cache = new Map();
    active = null;
    evictTimer = null;
    flushTimer = null;
    replaced = false;
    absorbedDims = null;
    finalDims = null;
    preparing = false;
    pendingPrepare = null;
    renderWaiters = [];
    disposed = false;
    counts = {
        prepares: 0,
        prepareHits: 0,
        evictions: 0,
        flushes: 0,
        freezes: 0,
        hintlessFreezes: 0,
        blurOnlyFreezes: 0,
        scaleFreezes: 0,
        declinedFreezes: 0,
        absorbed: 0,
        replaces: 0,
        reveals: 0,
        revealFades: 0,
        settleRetries: 0,
        errors: 0,
        lastPrepareMs: null,
        frozen: false,
    };
    constructor(adapter, options = {}, store) {
        this.adapter = adapter;
        this.enabled = options.enabled !== false;
        this.freezeWithoutFinalSize = options.freezeWithoutFinalSize !== false;
        this.swapTolerancePx =
            options.swapTolerancePx ?? DEFAULT_SWAP_TOLERANCE_PX;
        this.blurTransition = options.blurTransition ?? {};
        this.blurOnly = options.blurOnly === true;
        this.scaleResize = options.scaleResize ?? {};
        this.bitmapCapable = !!adapter.captureBitmap;
        this.blurPx =
            options.blurPx && options.blurPx > 0
                ? options.blurPx
                : DEFAULT_BLUR_PX;
        this.revealBlur = options.revealBlur ?? {};
        this.revealCrossfade = options.revealCrossfade ?? {};
        this.store =
            store ??
                new SnapshotStore({
                    getRoot: () => adapter.getRoot(),
                    getMount: () => adapter.getMount(),
                    resize: dims => adapter.resize(dims.width, dims.height),
                    waitForRender: () => this.waitForRender(),
                    captureBitmap: adapter.captureBitmap
                        ? () => adapter.captureBitmap()
                        : undefined,
                });
        if (this.bitmapCapable) {
            this.evictTimer = setInterval(() => this.evictStale(), CACHE_EVICT_POLL_MS);
            this.flushTimer = setInterval(() => this.flushCache(), CACHE_FLUSH_MS);
        }
    }
    notifyRenderComplete() {
        const waiters = this.renderWaiters;
        this.renderWaiters = [];
        waiters.forEach(resolve => resolve());
    }
    stats() {
        return { ...this.counts, frozen: this.frozen() };
    }
    debugSnapshots() {
        const info = (snap) => snap
            ? {
                width: snap.canvas.width,
                height: snap.canvas.height,
                dims: { ...snap.dims },
            }
            : null;
        return {
            canvas1: info(this.cache.get(sizeKey(this.adapter.getSize())) ?? null),
            canvas2: info(this.store.canvas2()),
            cacheKeys: [...this.cache.keys()],
        };
    }
    dispose() {
        this.disposed = true;
        if (this.evictTimer !== null)
            clearInterval(this.evictTimer);
        if (this.flushTimer !== null)
            clearInterval(this.flushTimer);
        if (this.scaleUpdateTimer !== null) {
            clearTimeout(this.scaleUpdateTimer);
            this.scaleUpdateTimer = null;
        }
        this.cancelRevealFade();
        this.overlay?.release();
        this.overlay = null;
        this.active = null;
        this.cache.clear();
        this.preparedScaleClone = null;
        this.notifyRenderComplete();
        this.store.clear();
    }
    invalidate() {
        this.cache.clear();
        this.preparedScaleClone = null;
        if (!this.frozen())
            this.store.clear();
    }
    async prepare(final = null) {
        if (this.disposed || !this.enabled)
            return;
        if (this.blurOnly)
            return;
        if (this.frozen() || this.preparing) {
            this.pendingPrepare = { final: final ? { ...final } : null };
            return;
        }
        if (this.scalePath()) {
            const root = this.adapter.getRoot();
            if (!root || isSnapshotMaskNode(root))
                return;
            const rect = root.getBoundingClientRect();
            if (!(rect.width > 0) || !(rect.height > 0))
                return;
            const scaleT0 = performance.now();
            this.preparedScaleClone = prepareScaledClone(root, this.adapter.getSize());
            this.counts.prepares += 1;
            this.counts.lastPrepareMs = Math.round(performance.now() - scaleT0);
            return;
        }
        if (!this.bitmapCapable)
            return;
        this.preparing = true;
        const t0 = performance.now();
        try {
            const initial = this.adapter.getSize();
            if (this.cacheGet(initial)) {
                this.counts.prepareHits += 1;
            }
            else {
                this.cachePut(await this.store.captureInitial(initial));
            }
            if (final) {
                await this.store.captureFinal(initial, final);
            }
            this.counts.prepares += 1;
            this.counts.lastPrepareMs = Math.round(performance.now() - t0);
        }
        catch (err) {
            this.counts.errors += 1;
            logger.warn('[snapshot-v2] prepare failed', err);
        }
        finally {
            this.preparing = false;
            this.flushPendingPrepare();
        }
    }
    freeze(final) {
        if (this.frozen())
            return true;
        if (!this.canFreeze(final)) {
            this.counts.declinedFreezes += 1;
            return false;
        }
        this.cancelRevealFade();
        const mount = this.adapter.getMount();
        const root = this.adapter.getRoot();
        this.frozenDims = { ...this.adapter.getSize() };
        this.lastBlurPx = 0;
        this.scaledBurst = this.scalePath();
        if (this.scaledBurst) {
            this.active = null;
            const prepared = this.takeFreshPreparedClone();
            if (prepared)
                this.counts.prepareHits += 1;
            const scaleMask = coverWithScaledClone(mount, root, this.frozenDims, this.scaleTransition(), this.blurTransition, this.scaleResize.scrollableSelectors ?? [], prepared);
            if (final)
                scaleMask.scaleTo(final);
            this.overlay = scaleMask;
            this.counts.scaleFreezes += 1;
        }
        else if (this.blurOnly) {
            this.active = null;
            this.overlay = blurInPlace(root, this.blurTransition);
            this.counts.blurOnlyFreezes += 1;
        }
        else {
            this.active = this.cacheGet(this.adapter.getSize());
            this.overlay = coverWithBitmap(mount, root, this.active.canvas, this.blurTransition);
        }
        this.replaced = false;
        this.absorbedDims = null;
        this.finalDims = !this.blurOnly && final ? { ...final } : null;
        if (!this.finalDims) {
            this.seedBlurPx = Math.min(FREEZE_SEED_BLUR_PX, this.blurPx);
            this.lastBlurPx = this.seedBlurPx;
            this.overlay.setBlur(this.lastBlurPx);
        }
        else {
            this.seedBlurPx = 0;
        }
        this.counts.freezes += 1;
        if (!this.finalDims)
            this.counts.hintlessFreezes += 1;
        return true;
    }
    scalePath() {
        return (!this.blurOnly &&
            !this.bitmapCapable &&
            this.scaleResize.enabled !== false);
    }
    scaleTransition() {
        return {
            ...(this.scaleResize.durationMs !== undefined
                ? { durationMs: this.scaleResize.durationMs }
                : {}),
            ...(this.scaleResize.easing !== undefined
                ? { easing: this.scaleResize.easing }
                : {}),
            ...(this.scaleResize.transitionDisabled !== undefined
                ? { disabled: this.scaleResize.transitionDisabled }
                : {}),
        };
    }
    scheduleScaleUpdate() {
        if (this.scaleUpdateTimer !== null)
            return;
        const wait = Math.max(0, SCALE_UPDATE_COALESCE_MS - (Date.now() - this.lastScaleUpdateAt));
        this.scaleUpdateTimer = setTimeout(() => {
            this.scaleUpdateTimer = null;
            this.applyScaleUpdate();
        }, wait);
    }
    applyScaleUpdate() {
        this.lastScaleUpdateAt = Date.now();
        if (!this.scaledBurst ||
            this.finalDims ||
            !this.absorbedDims ||
            !this.frozenDims ||
            !this.overlay) {
            return;
        }
        this.overlay.scaleTo(this.absorbedDims);
        this.lastBlurPx = Math.max(this.seedBlurPx, stretchBlurPx(this.frozenDims, this.absorbedDims, this.blurPx));
        this.overlay.setBlur(this.lastBlurPx);
    }
    flushScaleUpdate() {
        if (this.scaleUpdateTimer !== null) {
            clearTimeout(this.scaleUpdateTimer);
            this.scaleUpdateTimer = null;
            this.applyScaleUpdate();
        }
    }
    canFreeze(final) {
        if (this.disposed || !this.enabled)
            return false;
        const current = this.adapter.getSize();
        if (!(current.width > 0) || !(current.height > 0))
            return false;
        const root = this.adapter.getRoot();
        if (!root || isSnapshotMaskNode(root))
            return false;
        if (this.scalePath())
            return final ? true : this.freezeWithoutFinalSize;
        if (this.blurOnly)
            return true;
        if (!this.bitmapCapable)
            return false;
        if (!this.cacheGet(this.adapter.getSize()))
            return false;
        if (!final)
            return this.freezeWithoutFinalSize;
        const canvas2 = this.store.canvas2();
        return !!canvas2 && sameDims(canvas2.dims, final);
    }
    resize(width, height) {
        if (this.disposed)
            return;
        if (!this.frozen()) {
            this.adapter.resize(width, height);
            return;
        }
        this.absorbedDims = { width, height };
        this.counts.absorbed += 1;
        if (this.scaledBurst) {
            if (this.finalDims) {
                return;
            }
            this.scheduleScaleUpdate();
            return;
        }
        if (!this.finalDims) {
            this.lastBlurPx = Math.max(this.seedBlurPx, stretchBlurPx(this.frozenDims, this.absorbedDims, this.blurPx));
            this.overlay.setBlur(this.lastBlurPx);
            return;
        }
        const canvas2 = this.store.canvas2();
        if (!this.replaced &&
            canvas2 &&
            nearDims(canvas2.dims, this.absorbedDims, this.swapTolerancePx)) {
            this.overlay.swapTo(canvas2.canvas);
            this.replaced = true;
            this.counts.replaces += 1;
        }
    }
    async unfreeze() {
        if (!this.frozen())
            return;
        this.flushScaleUpdate();
        try {
            await this.settleBehindOverlay();
        }
        finally {
            const overlay = this.overlay;
            this.overlay = null;
            this.startReveal(overlay);
            this.counts.reveals += 1;
            const landed = this.absorbedDims ?? this.finalDims;
            const canvas2 = this.store.canvas2();
            if (!this.scaledBurst) {
                if (this.replaced &&
                    landed &&
                    canvas2 &&
                    sameDims(canvas2.dims, landed)) {
                    this.store.promote();
                    const promoted = this.store.canvas1();
                    if (promoted)
                        this.cachePut(promoted);
                }
                else {
                    this.store.clear();
                }
            }
            this.scaledBurst = false;
            this.active = null;
            this.absorbedDims = null;
            this.finalDims = null;
            this.frozenDims = null;
            this.replaced = false;
            this.flushPendingPrepare();
        }
    }
    async settleBehindOverlay() {
        let target = this.absorbedDims ?? this.finalDims;
        for (let attempt = 0; attempt < MAX_SETTLE_RELAYOUTS; attempt += 1) {
            if (!target || sameDims(target, this.adapter.getSize())) {
                return;
            }
            const rendered = this.waitForRender();
            this.adapter.resize(target.width, target.height);
            const renderedInTime = await withRenderTimeout(rendered, target);
            if (!renderedInTime) {
                return;
            }
            const latest = this.absorbedDims;
            if (!latest || sameDims(latest, target)) {
                return;
            }
            this.counts.settleRetries += 1;
            target = { ...latest };
        }
    }
    startReveal(overlay) {
        if (!overlay)
            return;
        const crossfade = this.disposed ||
            this.blurOnly ||
            this.revealCrossfade.enabled === false
            ? null
            : {
                durationMs: this.revealCrossfade.durationMs ??
                    DEFAULT_REVEAL_CROSSFADE_MS,
                easing: this.revealCrossfade.easing ??
                    DEFAULT_REVEAL_CROSSFADE_EASING,
            };
        const takeDown = () => crossfade ? overlay.dismiss(crossfade) : overlay.release();
        const blurActive = this.revealBlur.enabled !== false &&
            !this.disposed &&
            this.lastBlurPx > 0;
        if (!blurActive) {
            takeDown();
            return;
        }
        this.revealFade = showRevealTransition({
            target: this.adapter.getMount(),
            fromPx: this.lastBlurPx,
            blurDurationMs: this.revealBlur.durationMs ?? DEFAULT_REVEAL_BLUR_MS,
            blurEasing: this.revealBlur.easing ?? DEFAULT_REVEAL_EASING,
            beforeCommit: takeDown,
        });
        this.counts.revealFades += 1;
    }
    cancelRevealFade() {
        this.revealFade?.cancel();
        this.revealFade = null;
    }
    cacheGet(dims) {
        const key = sizeKey(dims);
        const snap = this.cache.get(key);
        if (!snap)
            return null;
        this.cache.delete(key);
        this.cache.set(key, snap);
        return snap;
    }
    cachePut(snap) {
        const key = sizeKey(snap.dims);
        this.cache.delete(key);
        this.cache.set(key, { canvas: snap.canvas, dims: { ...snap.dims } });
    }
    evictStale() {
        if (this.disposed || this.frozen())
            return;
        if (this.cache.size <= CACHE_MAX)
            return;
        if (this.cache.has(sizeKey(this.adapter.getSize())))
            return;
        const oldest = this.cache.keys().next().value;
        if (oldest !== undefined) {
            this.cache.delete(oldest);
            this.counts.evictions += 1;
        }
    }
    flushCache() {
        if (this.disposed || this.frozen())
            return;
        if (this.scalePath()) {
            if (this.preparedScaleClone) {
                this.preparedScaleClone = null;
                this.counts.flushes += 1;
                void this.prepare(null);
            }
            return;
        }
        if (this.cache.size === 0)
            return;
        this.cache.clear();
        this.counts.flushes += 1;
        void this.prepare(null);
    }
    takeFreshPreparedClone() {
        const prepared = this.preparedScaleClone;
        this.preparedScaleClone = null;
        if (!prepared)
            return null;
        if (!(parseFloat(prepared.clone.style.width) > 0))
            return null;
        return nearDims(prepared.dims, this.adapter.getSize(), PREPARED_CLONE_TOLERANCE_PX)
            ? prepared
            : null;
    }
    flushPendingPrepare() {
        const pending = this.pendingPrepare;
        this.pendingPrepare = null;
        if (pending && !this.disposed && !this.frozen()) {
            void this.prepare(pending.final);
        }
    }
    frozen() {
        return this.overlay !== null;
    }
    waitForRender() {
        return new Promise(resolve => this.renderWaiters.push(resolve));
    }
}
export default SnapshotManagerV2;
//# sourceMappingURL=snapshot-manager.js.map